  

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Cartouche {
  id: number;
  nom?: string;
  lot_cartouche_id?: number;
  chimie_id?: number;
  utilise?: boolean;
  inutilisable?: boolean;
}

export interface CartouchePayload {
  nom?: string;
  lot_cartouche_id?: number;
  chimie_id?: number;
  utilise?: boolean;
  inutilisable?: boolean;
}

export interface CartoucheGroupesPayload {
  groupe_amorce_ids: number[];
}

export interface CartoucheLivraisonItem {
  id: number;
  nom: string;
  statut_livraison: number | null;
  statut_livraison_nom: string | null;
}

export interface CartoucheSansGroupeItem {
  id: number;
  nom: string;
  statut_livraison: number | null;
  statut_livraison_nom: string | null;
}

export interface AttributionItem {
  cartouche_id: number;
  cartouche_nom: string;
  groupe_amorce_id: number;
  groupe_amorce_nom: string;
  date_attribution: string | null;
  amorces_codes: string;
}

export interface AttributionExportItem {
  code_cartouche: string;
  groupe_amorces: string;
  codes_amorces: string;
  date_attribution: string | null;
  sequencage_commande?: boolean | null;
  date_sequencage?: string | null;
}

export interface CartouchesSequencageBatchPayload {
  cartouche_noms: string[];
  sequencage_commande?: boolean;
  date_sequencage?: string | null;
}

export interface CartouchesSequencageBatchResultItem {
  id: number;
  nom: string;
  sequencage_commande: boolean | null;
  date_sequencage: string | null;
}

export interface CartouchesSequencageBatchResult {
  updated: number;
  items: CartouchesSequencageBatchResultItem[];
}

export interface CartoucheSequencageCommandeeItem {
  id: number;
  nom: string;
  sequencage_commande: boolean | null;
  date_sequencage: string | null;
}

export interface CartoucheCommandeClientItem {
  numero_commande: string;
  client_identifiant: string;
}

export interface CartoucheDepartementItem {
  cartouche: string;
  departement: string;
}

@Injectable({ providedIn: 'root' })
export class CartouchesService {
  private readonly baseUrl = `${environment.apiBaseUrl}/api/cartouches`;
  private readonly disponiblesUrl = `${this.baseUrl}/disponibles`;

  constructor(private http: HttpClient) {}

  list(): Observable<Cartouche[]> {
    return this.http.get<{ items: Cartouche[] }>(this.baseUrl).pipe(map((res: { items: Cartouche[] }) => res.items));
  }

  get(id: number): Observable<Cartouche> {
    return this.http.get<Cartouche>(`${this.baseUrl}/${id}`);
  }

  create(payload: CartouchePayload): Observable<Cartouche> {
    return this.http.post<Cartouche>(this.baseUrl, payload);
  }

  update(id: number, payload: CartouchePayload): Observable<Cartouche> {
    return this.http.put<Cartouche>(`${this.baseUrl}/${id}`, payload);
  }

  getNom(id: number) {
    return this.http.get<{ nom: string }>(`${this.baseUrl}/${id}/nom`);
  }


  delete(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${id}`);
  }

  updateSequencageBatch(payload: CartouchesSequencageBatchPayload): Observable<CartouchesSequencageBatchResult> {
    return this.http.patch<CartouchesSequencageBatchResult>(`${this.baseUrl}/sequencage/batch`, payload);
  }

  listSequencageCommandees(): Observable<CartoucheSequencageCommandeeItem[]> {
    return this.http
      .get<{ items: CartoucheSequencageCommandeeItem[] }>(`${this.baseUrl}/sequencage/commandees`)
      .pipe(map((res: { items: CartoucheSequencageCommandeeItem[] }) => res.items ?? []));
  }

  autocompleteNom(q: string): Observable<Cartouche[]> {
    return this.http
      .get<{ items: Cartouche[] }>(`${this.baseUrl}?search=${encodeURIComponent(q)}`)
      .pipe(map((res: { items: Cartouche[] }) => res.items));
  }

  listDisponibles(): Observable<Cartouche[]> {
    return this.http
      .get<{ items: Array<{ id: number; nom: string }> }>(this.disponiblesUrl)
      .pipe(
        map((res: { items: Array<{ id: number; nom: string }> }) =>
          (res.items ?? []).map((item: { id: number; nom: string }) => ({ id: item.id, nom: item.nom }))
        )
      );
  }

  autocompleteNomDisponibles(q: string): Observable<Cartouche[]> {
    const needle = q.trim().toLowerCase();
    return this.listDisponibles().pipe(
      map((items: Cartouche[]) => {
        const filtered = items.filter((c: Cartouche) => String(c.nom ?? '').toLowerCase().includes(needle));
        // Si recherche vide, limiter à 10 premiers résultats
        return needle === '' ? filtered.slice(0, 10) : filtered;
      })
    );
  }

  getGroupesAmorces(cartoucheId: number): Observable<{ cartouche_id: number; groupe_amorce_ids: number[] }> {
    return this.http.get<{ cartouche_id: number; groupe_amorce_ids: number[] }>(
      `${this.baseUrl}/${cartoucheId}/groupes-amorces`
    );
  }

  updateGroupesAmorces(cartoucheId: number, payload: CartoucheGroupesPayload): Observable<{ cartouche_id: number; groupe_amorce_ids: number[] }> {
    return this.http.put<{ cartouche_id: number; groupe_amorce_ids: number[] }>(
      `${this.baseUrl}/${cartoucheId}/groupes-amorces`,
      payload
    );
  }

  listLivrees(): Observable<CartoucheLivraisonItem[]> {
    return this.http
      .get<{ items: CartoucheLivraisonItem[] }>(`${this.baseUrl}/livraisons/livrees`)
      .pipe(map((res: { items: CartoucheLivraisonItem[] }) => res.items ?? []));
  }

  listLivraisonsFields(): Observable<CartoucheLivraisonItem[]> {
    return this.http
      .get<{ items: CartoucheLivraisonItem[] }>(`${this.baseUrl}/livraisons/fields`)
      .pipe(map((res: { items: CartoucheLivraisonItem[] }) => res.items ?? []));
  }

  listRetardsCartouches(): Observable<any[]> {
    return this.http
      .get<{ items: any[] }>(`${environment.apiBaseUrl}/api/capteurs/retards/cartouches`)
      .pipe(map((res: { items: any[] }) => res.items ?? []));
  }

  listLivreesSansGroupeAmorces(): Observable<CartoucheSansGroupeItem[]> {
    return this.http
      .get<{ items: CartoucheSansGroupeItem[] }>(`${this.baseUrl}/livraisons/livrees/sans-groupe-amorces`)
      .pipe(map((res: { items: CartoucheSansGroupeItem[] }) => res.items ?? []));
  }

  listAttributions(): Observable<AttributionItem[]> {
    return this.http
      .get<{ items: AttributionItem[] }>(`${this.baseUrl}/attributions`)
      .pipe(map((res: { items: AttributionItem[] }) => res.items ?? []));
  }

  deleteAttribution(cartoucheId: number, groupeAmorceId: number): Observable<{ deleted: boolean; cartouche_id: number; groupe_amorce_id: number }> {
    return this.http.delete<{ deleted: boolean; cartouche_id: number; groupe_amorce_id: number }>(
      `${this.baseUrl}/${cartoucheId}/groupes-amorces/${groupeAmorceId}`
    );
  }

  exportAttributionsData(): Observable<AttributionExportItem[]> {
    return this.http
      .get<{ items: AttributionExportItem[] }>(`${this.baseUrl}/attributions/export`)
      .pipe(map((res: { items: AttributionExportItem[] }) => res.items ?? []));
  }

  autocompleteNomLivrees(q: string): Observable<Cartouche[]> {
    const needle = q.trim().toLowerCase();
    return this.listLivrees().pipe(
      map((items: CartoucheLivraisonItem[]) => {
        const filtered = items
          .filter((item: CartoucheLivraisonItem) =>
            String(item.nom ?? '').toLowerCase().includes(needle)
          )
          .map((item: CartoucheLivraisonItem) => ({ id: item.id, nom: item.nom }));
        // Si recherche vide, limiter à 10 premiers résultats
        return needle === '' ? filtered.slice(0, 10) : filtered;
      })
    );
  }

  getCommandesByNom(cartoucheNom: string): Observable<CartoucheCommandeClientItem[]> {
    return this.http
      .get<{ items: CartoucheCommandeClientItem[] }>(
        `${this.baseUrl}/commandes/par-nom?nom_cartouche=${encodeURIComponent(cartoucheNom)}`
      )
      .pipe(map((res: { items: CartoucheCommandeClientItem[] }) => res.items ?? []));
  }

  getDepartementByNom(cartoucheNom: string): Observable<CartoucheDepartementItem> {
    return this.http.get<CartoucheDepartementItem>(
      `${this.baseUrl}/departement-par-nom?nom_cartouche=${encodeURIComponent(cartoucheNom)}`
    );
  }
}
